/**
 * Lógica del catálogo ANMAT (SNT) — inferencia del evento default y filtrado
 * de eventos compatibles por contexto regulatorio.
 *
 * El catálogo SNT define ~524 eventos, cada uno con la tupla:
 *   (informador, evento, origen, destino)
 * y un ID único. Para cada despacho/recepción hay que pasar el ID correcto.
 *
 * Estas funciones son puras: reciben el catálogo + el contexto y devuelven
 * el evento aplicable. NO acceden a store global.
 */

import type { EventoAnmat, TipoActor, Ubicacion } from '../types';

// =============================================================================
// CONSTANTES
// =============================================================================

/**
 * Texto del evento default cuando emitimos un despacho (informador = origen).
 * Confirmado con stakeholder regulatorio.
 */
export const EVENTO_TEXTO_DEFAULT_ENVIANDO =
  'DISTRIBUCION DEL PRODUCTO A UN ESLABON POSTERIOR';

/** Texto del evento default cuando recibimos (informador = destino). */
export const EVENTO_TEXTO_DEFAULT_RECIBIENDO =
  'RECEPCION DE PRODUCTO DESDE UN ESLABON ANTERIOR';

/** Eventos directos del catálogo SNT — usados como atajos en pickeo. */
export const EV_EVEREX_TO_FARMACIA = 130; // LABORATORIO → FARMACIA
export const EV_EVEREX_TO_DROGUERIA = 128; // LABORATORIO → DROGUERIA
export const EV_EVEREX_TO_HOSPITAL = 129; // LABORATORIO → ESTABLECIMIENTO ASISTENCIAL

// =============================================================================
// DEFAULT POR TUPLA (override custom)
// =============================================================================

/**
 * Mapa de defaults custom seteados por el operador desde la vista
 * maestros-eventos. Key: clave compuesta `informador||origen||destino`.
 * Value: id del evento que reemplaza al hardcoded para esa tupla.
 *
 * Si una tupla no está en el mapa, se cae al hardcoded
 * (`EVENTO_TEXTO_DEFAULT_*`).
 */
export type EventosDefaultCustom = Record<string, number>;

export function eventoAnmatDefaultKey(
  informador: TipoActor | null | undefined,
  origen: TipoActor | null | undefined,
  destino: TipoActor | null | undefined,
): string {
  return `${informador ?? ''}||${origen ?? ''}||${destino ?? '__null'}`;
}

/**
 * ¿`evento` es el default actual para su tupla (custom o hardcoded)?
 * Usado por la vista maestros-eventos para mostrar la marca "⭐ Default".
 */
export function esEventoDefaultActual(
  evento: EventoAnmat | null | undefined,
  customMap: EventosDefaultCustom = {},
): boolean {
  if (!evento) return false;
  const clave = eventoAnmatDefaultKey(evento.informador, evento.origen, evento.destino);
  // Hay custom para esta tupla → solo es default si coincide con este evento
  if (customMap[clave] !== undefined) return customMap[clave] === evento.id;
  // Sin custom: fallback al hardcoded — solo aplica a tuplas con destino concreto
  if (!evento.destino) return false;
  if (evento.informador === evento.origen) {
    return evento.evento === EVENTO_TEXTO_DEFAULT_ENVIANDO;
  }
  if (evento.informador === evento.destino) {
    return evento.evento === EVENTO_TEXTO_DEFAULT_RECIBIENDO;
  }
  return false;
}

// =============================================================================
// INFERENCIA DEL EVENTO
// =============================================================================

export type DireccionTransaccion = 'enviando' | 'recibiendo';

export interface InferirEventoInput {
  ubicacionEmisora: Ubicacion;
  direccion: DireccionTransaccion;
  /** Tipo de actor del destino (cuando estamos enviando). */
  destinoTipoActor?: TipoActor | null;
  /** Tipo de actor del origen (cuando estamos recibiendo). */
  origenTipoActor?: TipoActor | null;
}

/**
 * Devuelve el evento del catálogo que matchea el contexto regulatorio.
 *
 * Orden de resolución:
 *   1. Custom default seteado en la tupla `(informador, origen, destino)`.
 *   2. Hardcoded `EVENTO_TEXTO_DEFAULT_*` para esa misma tupla.
 *   3. (CR-5) Para `recibiendo`: fallback a evento de DEVOLUCION si existe.
 *   4. Cualquier evento del informador con origen+destino matching.
 *   5. `null` si no hay ninguno.
 *
 * El caso 3 cubre el bug donde tuplas como `LAB ← DROGUERIA` no tienen
 * default de "recepción normal" en el catálogo SNT.
 */
export function inferirEventoAnmat(
  input: InferirEventoInput,
  eventosAnmat: readonly EventoAnmat[],
  customMap: EventosDefaultCustom = {},
): EventoAnmat | null {
  const { ubicacionEmisora, direccion, destinoTipoActor, origenTipoActor } = input;
  if (!ubicacionEmisora?.tipoActor) return null;

  const miTipoActor = ubicacionEmisora.tipoActor;
  const eventoTexto =
    direccion === 'enviando' ? EVENTO_TEXTO_DEFAULT_ENVIANDO : EVENTO_TEXTO_DEFAULT_RECIBIENDO;
  const origen = direccion === 'enviando' ? miTipoActor : (origenTipoActor ?? null);
  const destino = direccion === 'enviando' ? (destinoTipoActor ?? null) : miTipoActor;

  // 1. Custom default
  const clave = eventoAnmatDefaultKey(miTipoActor, origen, destino);
  const customId = customMap[clave];
  if (customId !== undefined) {
    const custom = eventosAnmat.find((e) => e.id === customId);
    if (custom) return custom;
  }

  // 2. Hardcoded default
  const match = eventosAnmat.find(
    (e) =>
      e.informador === miTipoActor &&
      e.origen === origen &&
      e.destino === destino &&
      e.evento === eventoTexto,
  );
  if (match) return match;

  // 3. Fallback CR-5: para recibiendo, intentar DEVOLUCION
  if (direccion === 'recibiendo') {
    const devolucion = eventosAnmat.find(
      (e) =>
        e.informador === miTipoActor &&
        e.origen === origen &&
        e.destino === destino &&
        typeof e.evento === 'string' &&
        e.evento.indexOf('DEVOLUCION') !== -1,
    );
    if (devolucion) return devolucion;
  }

  // 4. Cualquier evento que coincida en (informador, origen, destino)
  return (
    eventosAnmat.find(
      (e) => e.informador === miTipoActor && e.origen === origen && e.destino === destino,
    ) ?? null
  );
}

// =============================================================================
// EVENTOS COMPATIBLES (poblado del dropdown de override)
// =============================================================================

export interface EventosCompatiblesInput extends InferirEventoInput {
  /** Marca explícita para incluir eventos terminales (destino null). */
  incluirDestructora?: boolean;
}

/**
 * Devuelve TODOS los eventos del catálogo compatibles con el contexto
 * regulatorio, en el orden esperado por la UI:
 *
 *   1. Eventos con destino que matchea (informador + origen + destino).
 *   2. Eventos sin destino (destrucción, robo, vencimiento, etc.) — solo
 *      cuando estamos enviando (un receptor no puede destruir al recibir).
 *
 * Si el destino es desconocido (empresa destructora sin tipoActor) o se pasa
 * `incluirDestructora: true`, retorna solo los eventos terminales del
 * informador.
 */
export function getEventosCompatibles(
  input: EventosCompatiblesInput,
  eventosAnmat: readonly EventoAnmat[],
): EventoAnmat[] {
  const { ubicacionEmisora, direccion, destinoTipoActor, origenTipoActor, incluirDestructora } =
    input;
  if (!ubicacionEmisora?.tipoActor) return [];

  const informador = ubicacionEmisora.tipoActor;
  const origen = direccion === 'enviando' ? informador : (origenTipoActor ?? null);
  const destino = direccion === 'enviando' ? (destinoTipoActor ?? null) : informador;

  // Caso especial: destino es empresa destructora (sin tipoActor)
  if (incluirDestructora || destino === null || destino === undefined) {
    return eventosAnmat.filter(
      (e) => e.informador === informador && e.origen === informador && !e.destino,
    );
  }

  const conDestino = eventosAnmat.filter(
    (e) => e.informador === informador && e.origen === origen && e.destino === destino,
  );

  const sinDestino =
    direccion === 'enviando'
      ? eventosAnmat.filter(
          (e) => e.informador === informador && e.origen === informador && !e.destino,
        )
      : [];

  return [...conDestino, ...sinDestino];
}

/**
 * Agrupa una lista de eventos por su texto, útil para renderizar `<optgroup>`
 * o secciones colapsables.
 */
export function agruparEventosPorTexto(
  eventos: readonly EventoAnmat[],
): Array<{ texto: string; items: EventoAnmat[] }> {
  const grupos = new Map<string, EventoAnmat[]>();
  for (const e of eventos) {
    let bucket = grupos.get(e.evento);
    if (!bucket) {
      bucket = [];
      grupos.set(e.evento, bucket);
    }
    bucket.push(e);
  }
  return Array.from(grupos.entries()).map(([texto, items]) => ({ texto, items }));
}
